import { useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import { ApiRouteHelpers } from "../../common/root/apiRoutes";
import {
  RegulationRequestTypeDto,
  WorkflowType,
  FormType,
  RegulationSlug,
  RegionGroup,
} from "../../common/service/server/dto/RegulationRequestTypeDto";

/**
 * Fetch regulation request types from the API
 * @returns Promise<RegulationRequestTypeDto[]>
 */
const fetchRegulationRequestTypes = async (): Promise<RegulationRequestTypeDto[]> => {
  const response = await fetch(ApiRouteHelpers.regulation.requestTypes());

  if (!response.ok) {
    throw new Error(`Failed to fetch regulation request types: ${response.status}`);
  }

  const typesData: RegulationRequestTypeDto[] = await response.json();

  // Enhance data with computed slugs for URL-friendly operations
  return typesData.map((type) => ({
    ...type,
    requestTypeSlug: `${type.regulationLaw.slug.toLowerCase()}_${type.workflowType.toLowerCase()}`,
  }));
};

/**
 * Hook to fetch and cache regulation request types with React Query
 * Provides filtering and caching with automatic background updates
 */
export const useRegulationRequestTypes = () => {
  // Use React Query for optimized caching and background updates
  const {
    data: regulationRequestTypes = [],
    isLoading: loading,
    error: queryError,
  } = useQuery({
    queryKey: ["regulation-request-types"],
    queryFn: fetchRegulationRequestTypes,
    staleTime: 5 * 60 * 1000, // 5 minutes
    cacheTime: 30 * 60 * 1000, // 30 minutes
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });

  const error = queryError ? (queryError as Error).message : null;

  // Efficient filtering function with multiple criteria including RegionGroup support
  const filterTypes = useCallback(
    (criteria: {
      regulationSlugs?: RegulationSlug[];
      regionGroups?: RegionGroup[];
      workflowTypes?: WorkflowType[];
      formTypes?: FormType[];
      requireVerification?: boolean;
    }): RegulationRequestTypeDto[] => {
      return regulationRequestTypes.filter((type) => {
        if (
          criteria.regulationSlugs &&
          !criteria.regulationSlugs.includes(type.regulationLaw.slug)
        ) {
          return false;
        }
        if (
          criteria.regionGroups &&
          type.regulationLaw.region &&
          !criteria.regionGroups.includes(type.regulationLaw.region.regionGroup)
        ) {
          return false;
        }
        if (criteria.workflowTypes && !criteria.workflowTypes.includes(type.workflowType)) {
          return false;
        }
        if (criteria.formTypes && !criteria.formTypes.includes(type.formType)) {
          return false;
        }
        if (
          criteria.requireVerification !== undefined &&
          type.requireVerification !== criteria.requireVerification
        ) {
          return false;
        }
        return true;
      });
    },
    [regulationRequestTypes],
  );

  return {
    // Core data
    regulationRequestTypes,
    loading,
    error,

    // Filtering function
    filterTypes,
  };
};
